import { Link } from "react-router-dom";
import type { Site } from "@/mocks/sites";
import SiteLogo from "@/components/feature/SiteLogo";
import { formatVisits } from "@/utils/site";

type Props = {
  site: Site;
  className?: string;
};

export default function SiteCard({ site, className = "" }: Props) {
  return (
    <article
      className={`group relative flex flex-col rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/40 ${className}`}
    >
      <Link
        to={`/detail/${site.id}`}
        className="absolute inset-0 rounded-lg"
        aria-label={`查看 ${site.name} 的详情`}
      ></Link>

      <div className="pointer-events-none flex items-start gap-3">
        <SiteLogo site={site} size={48} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-semibold text-foreground-950">{site.name}</h3>
            {site.featured ? (
              <span className="shrink-0 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-700">
                精选
              </span>
            ) : null}
          </div>
          <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-foreground-600">{site.desc}</p>
        </div>
      </div>

      <div className="pointer-events-none mt-3 flex flex-wrap gap-1.5">
        {site.tags.slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="rounded-md bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-700"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="mt-auto flex items-center justify-between pt-3">
        <span className="flex items-center gap-1 text-xs text-foreground-500">
          <i className="ri-eye-line"></i>
          {formatVisits(site.visits)} 次访问
        </span>
        <a
          href={site.url}
          target="_blank"
          rel="noreferrer"
          className="relative z-10 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-external-link-line"></i>访问
        </a>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] max-h-0 overflow-hidden rounded-b-lg bg-foreground-950/95 px-4 opacity-0 transition-all duration-300 group-hover:max-h-44 group-hover:pb-3 group-hover:pt-2 group-hover:opacity-100">
        <p className="pr-2 text-[11px] leading-relaxed text-background-50/90">{site.detail}</p>
      </div>
    </article>
  );
}